import React, {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
  ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const THEME_STORAGE_KEY = 'ap-theme';
const THEME_COLOR_BY_MODE: Record<ThemeMode, string> = {
  light: '#f8fafc',
  dark: '#0f172a',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme);

  // useLayoutEffect (not useEffect) so the attribute/meta updates land
  // before the browser paints the new theme -- otherwise a toggle (or the
  // fixed, full-screen loading spinner) could show a frame of the old
  // theme's colors first.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);

    // Keep the mobile browser chrome / PWA status bar tint in sync too, so
    // it doesn't stay black while the app itself is in its light theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR_BY_MODE[theme]);
  }, [theme]);

  const setTheme = (next: ThemeMode) => setThemeState(next);

  const toggleTheme = () =>
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
